import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAuthStore } from '@/store/authStore';
import { isValidUUID } from '@/lib/utils';
import type { NameMatch } from '../types';

export function useMatches() {
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { user } = useAuthStore();

  // In baby mode, use lastCompletedPregnancy; otherwise use currentPregnancy
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const rawPregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);

  // Validate that the pregnancy ID is a valid UUID before using it
  const activePregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;
  const [userFirstName, setUserFirstName] = useState<string | null>(null);

  // Fetch current user's first name for local state updates
  useEffect(() => {
    async function fetchUserName() {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('first_name')
        .eq('id', user.id)
        .single();
      if (data) {
        setUserFirstName(data.first_name);
      }
    }
    fetchUserName();
  }, [user]);
  const [matches, setMatches] = useState<NameMatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'shortlisted'>('all');

  // Fetch matches
  const fetchMatches = useCallback(async () => {
    if (!activePregnancyId) return;

    setIsLoading(true);
    try {
      let query = supabase
        .from('name_matches')
        .select('*, baby_name:baby_names(*), notes_author:profiles!name_matches_notes_updated_by_fkey(id, first_name)')
        .eq('pregnancy_id', activePregnancyId)
        .order('matched_at', { ascending: false });

      if (filter === 'shortlisted') {
        query = query.eq('is_shortlisted', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setMatches(data || []);
    } catch (err) {
      console.error('Error fetching matches:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activePregnancyId, filter]);

  // Initial fetch
  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Toggle shortlist
  const toggleShortlist = useCallback(async (matchId: string, current: boolean) => {
    try {
      const { error } = await supabase
        .from('name_matches')
        .update({ is_shortlisted: !current })
        .eq('id', matchId);

      if (error) throw error;

      // Update local state
      setMatches((prev) =>
        prev.map((m) =>
          m.id === matchId ? { ...m, is_shortlisted: !current } : m
        )
      );
    } catch (err) {
      console.error('Error toggling shortlist:', err);
    }
  }, []);

  // Update notes
  const updateNotes = useCallback(async (matchId: string, notes: string) => {
    if (!user) return;

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('name_matches')
        .update({
          notes,
          notes_updated_by: user.id,
          notes_updated_at: now
        })
        .eq('id', matchId);

      if (error) throw error;

      // Update local state with author info
      setMatches((prev) =>
        prev.map((m) => (m.id === matchId ? {
          ...m,
          notes,
          notes_updated_by: user.id,
          notes_updated_at: now,
          notes_author: { id: user.id, first_name: userFirstName }
        } : m))
      );
    } catch (err) {
      console.error('Error updating notes:', err);
    }
  }, [user, userFirstName]);

  // Remove match
  const removeMatch = useCallback(async (matchId: string) => {
    try {
      const { error } = await supabase
        .from('name_matches')
        .delete()
        .eq('id', matchId);

      if (error) throw error;

      // Update local state
      setMatches((prev) => prev.filter((m) => m.id !== matchId));
    } catch (err) {
      console.error('Error removing match:', err);
    }
  }, []);

  // Stats
  const totalMatches = matches.length;
  const shortlistedCount = matches.filter((m) => m.is_shortlisted).length;

  return {
    matches,
    isLoading,
    filter,
    setFilter,
    totalMatches,
    shortlistedCount,
    toggleShortlist,
    updateNotes,
    removeMatch,
    refetch: fetchMatches,
  };
}
